import { useQuery } from '@tanstack/react-query';
import { fetchShifts, type ShiftRow } from '../../lib/api/shifts';
import { formatDate, formatNumber } from '../../lib/format';
import { useTableState } from '../../hooks/useTableState';
import { DataBoundary } from '../../components/DataBoundary';
import { DataTable, type Column } from '../../components/DataTable';
import { Badge, Card, Notice, TableSkeleton } from '../../components/primitives';
import { PageHeading } from '../layout/PageHeading';

const DIAS = ['', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];

/** `1,2,3,4,5` → `Lu a Vi`; `1,3,5` → `Lu, Mi, Vi`. */
function diasLegibles(dias: number[]): string {
  if (dias.length === 0) return '—';
  const ordenados = [...dias].sort((a, b) => a - b);

  // Se detecta el rango continuo: «Lu a Sá» se lee mucho mejor que seis abreviaturas.
  const esContinuo = ordenados.every((d, i) => i === 0 || d === ordenados[i - 1] + 1);
  if (esContinuo && ordenados.length > 2) {
    return `${DIAS[ordenados[0]]} a ${DIAS[ordenados[ordenados.length - 1]]}`;
  }
  return ordenados.map((d) => DIAS[d]).join(', ');
}

/**
 * Turnos planificados.
 *
 * ── Por qué esta pantalla importa ──────────────────────────────────────────
 * El turno es el denominador de los atrasos: define a qué hora se esperaba a cada
 * persona. Vivía solo en la base, y un indicador cuyo criterio no se puede consultar
 * es un indicador que hay que creer a ciegas.
 *
 * ── Lo que distingue con más énfasis ───────────────────────────────────────
 * Los turnos DERIVADOS de las marcaciones frente a los contractuales. No es una
 * etiqueta decorativa: un atraso medido contra un turno derivado compara a cada grupo
 * con su propio promedio, así que quien llega sistemáticamente tarde corre la
 * referencia y aparece puntual. Quien lea un porcentaje de atrasos necesita saber
 * cuál de los dos lo produjo.
 */
export function ShiftsPage(): JSX.Element {
  const table = useTableState({ pageSize: 50 });

  const consulta = useQuery({ queryKey: ['shifts'], queryFn: fetchShifts });

  const turnos = consulta.data?.shifts ?? [];
  const derivados = turnos.filter((t) => t.source === 'CALCULATED').length;
  const contractuales = turnos.filter((t) => t.source !== 'CALCULATED').length;

  const columns: Array<Column<ShiftRow>> = [
    { key: 'code', header: 'Código', render: (t) => t.code },
    { key: 'company', header: 'Empresa', render: (t) => t.company },
    {
      key: 'area',
      header: 'Área',
      // «Toda la empresa» y no un guion: el turno sin área SÍ aplica, a todo el
      // personal. Un guion se leería como «sin asignar».
      render: (t) => t.area ?? <span className="muted">Toda la empresa</span>,
    },
    {
      key: 'horario',
      header: 'Horario',
      render: (t) => (
        <>
          {t.startTime} – {t.endTime}
          {t.crossesMidnight && <span className="tiny muted"> (cruza medianoche)</span>}
        </>
      ),
    },
    { key: 'weekDays', header: 'Días', render: (t) => diasLegibles(t.weekDays) },
    {
      key: 'toleranceMinutes',
      header: 'Tolerancia',
      align: 'right',
      render: (t) => `${formatNumber(t.toleranceMinutes, 'integer')} min`,
    },
    {
      key: 'source',
      header: 'Origen',
      render: (t) =>
        t.source === 'CALCULATED' ? (
          <Badge tone="amber">Derivado</Badge>
        ) : (
          <Badge tone="green">Contractual</Badge>
        ),
    },
    {
      key: 'validFrom',
      header: 'Vigente desde',
      render: (t) => formatDate(t.validFrom),
    },
    {
      key: 'isActive',
      header: 'Estado',
      render: (t) => (t.isActive ? 'Activo' : <span className="muted">Inactivo</span>),
    },
  ];

  return (
    <>
      <PageHeading
        title="Turnos BUK"
        description="Horario planificado por empresa y área. Es el criterio con el que se calculan los atrasos y las inasistencias."
      />

      {derivados > 0 && (
        <Notice tone="partial">
          <strong>
            {formatNumber(derivados, 'integer')} de {formatNumber(turnos.length, 'integer')} turnos
            son DERIVADOS de las marcaciones.
          </strong>{' '}
          No son el horario pactado: se calcularon con la mediana de las entradas
          observadas. Un atraso medido contra un turno derivado compara a cada grupo con
          su propio promedio, así que un equipo que llega sistemáticamente tarde corre la
          referencia y aparece puntual.
          <br />
          <br />
          Sirven para orientarse mientras Gestión de Personas entrega los contractuales, y
          para detectar discrepancias cuando lleguen: si el turno real y el derivado
          difieren en media hora, esa media hora es el hallazgo. Se reemplazan con{' '}
          <code>scripts/cargar-turnos.ts</code>.
        </Notice>
      )}

      <Card title="Turnos registrados">
        <DataBoundary
          isLoading={consulta.isLoading}
          isError={consulta.isError}
          error={consulta.error}
          onRetry={() => void consulta.refetch()}
          isEmpty={turnos.length === 0}
          empty={{
            title: 'Sin turnos cargados',
            message:
              'Sin turno planificado no existe la hora de entrada esperada, y los atrasos ' +
              'y las inasistencias no son calculables. Cárguelos con scripts/cargar-turnos.ts.',
          }}
          skeleton={<TableSkeleton rows={8} />}
        >
          <>
            <p className="tiny muted" style={{ marginBottom: 10 }}>
              {formatNumber(contractuales, 'integer')} contractual(es) ·{' '}
              {formatNumber(derivados, 'integer')} derivado(s)
            </p>
            <DataTable
              caption="Turnos planificados por empresa y área"
              columns={columns}
              mode="client"
              rows={turnos}
              getRowId={(t) => t.id}
              state={table.state}
              onStateChange={table.setState}
              searchPlaceholder="Buscar por código, empresa o área…"
            />
          </>
        </DataBoundary>
      </Card>
    </>
  );
}
